import { Asset, Creator, FolderContent } from '@/api/generatedApi';
import { AssetNameWithExtension } from '@/components/AssetNameWithExtension';
import { AddToCompareButton } from '@/components/CompareCart';
import { Ellipsis } from '@/components/Ellipsis';
import { FolderIcon } from '@/components/FolderIcon';
import { Thumbnail } from '@/components/Thumbnail';
import { useGetCurrentUser } from '@/hooks/api/users/useGetCurrentUser';
import { useAssetPolling } from '@/hooks/useDataPolling/useAssetPolling';
import { useTenants } from '@/services/tenants/useTenants';
import { useOptimisticFolderName } from '@/hooks/useOptimisticFolderName';
import { getAssetFolderDirectory, getAssetName } from '@/utils/assets';
import { getAssetStateDisplayText } from '@/utils/assetStateDisplayText';
import { dateFormat, pluralize } from '@/utils/format';
import { Box, LinearProgress, styled, Tooltip } from '@mui/material';
import { GridRenderCellParams } from '@mui/x-data-grid-pro';
import { getFileExtension } from '@physna/supported-files';
import { Link, useSearchParams } from 'react-router-dom';
import { FindMatchesButton } from '../Matches/FindMatchesButton';
import { ThumbnailPreview } from '../ThumbnailPreview';
import { MatchData } from '@/types/matchData';

const AssetLink = styled(Link)(({ theme }) => ({
  flexShrink: 0,
  textDecoration: 'none',
  color: theme.palette.primary.main,
  '&:hover': {
    textDecoration: 'underline',
  },
}));

const handleLinkClick = (event: React.MouseEvent) => {
  event.stopPropagation();
};

export const RenderName = (parameters: GridRenderCellParams<FolderContent>) => {
  const { getPath } = useTenants();
  const [, setSearchParameters] = useSearchParams();
  const { data: asset } = useAssetPolling(parameters);
  const { setOptimisticFolder } = useOptimisticFolderName();

  const handleFolderClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (parameters.row.contentType === 'folder') {
      setOptimisticFolder(parameters.row.id, parameters.row.name);
    }
    setSearchParameters({ folderId: parameters.row.id });
  };

  return parameters.row.contentType === 'asset' ? (
    <Link
      to={getPath(`asset/${parameters.row.id}`)}
      style={{ textDecoration: 'none', color: 'inherit' }}
      onClick={handleLinkClick}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 1 }}>
        <ThumbnailPreview primaryAsset={parameters.row} placement='right' message={getAssetName(parameters.row)}>
          <Box sx={{ cursor: 'pointer' }}>
            <Thumbnail size={42} asset={parameters.row} noLink={true} />
          </Box>
        </ThumbnailPreview>
        <AssetNameWithExtension
          name={getAssetName(parameters.row)}
          path={parameters.row.path}
          extension={getFileExtension(parameters.row.path)}
          showAssemblyPill={asset.isAssembly}
          isRootAssembly={asset.isRootAssembly}
          isMissingDependencies={asset.isMissingDependencies}
        />
      </Box>
    </Link>
  ) : (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        cursor: 'pointer',
        '&:hover': {
          textDecoration: 'underline',
        },
      }}
      onClick={handleFolderClick}
    >
      <FolderIcon />
      <Ellipsis text={parameters.row.name} />
    </Box>
  );
};

export const AssetDetailsCell = ({
  asset,
  quickCompareAssetId,
  showFindMatchesButton = true,
  matchData,
  minThreshold,
}: {
  asset: Asset;
  quickCompareAssetId?: string;
  showFindMatchesButton?: boolean;
  matchData?: MatchData;
  minThreshold?: number;
}) => {
  const { permissions } = useGetCurrentUser();

  return asset.state === 'finished' ? (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gap: 1,
        alignItems: 'center',
        width: '100%',
        maxWidth: '170px',
      }}
    >
      {!permissions.canOnlySearch && (
        <>
          {showFindMatchesButton && <FindMatchesButton asset={asset} variant='icon' minThreshold={minThreshold} />}
          <AddToCompareButton asset={asset} variant='icon' quickCompare={quickCompareAssetId} matchData={matchData} />
        </>
      )}
    </Box>
  ) : (
    <Box display='flex' flexDirection='row' alignItems='center' gap={'8px'}>
      {asset.state === 'indexing' && (
        <LinearProgress sx={{ width: '38px' }} data-testid={`${asset.id}-indexing-progress`} />
      )}
      <Box overflow='hidden'>
        <Ellipsis text={getAssetStateDisplayText(asset.state)} />
      </Box>
    </Box>
  );
};

const RenderAssetDetails = (parameters: GridRenderCellParams<FolderContent>) => {
  const { data: asset } = useAssetPolling(parameters);

  return <AssetDetailsCell asset={asset} />;
};

export const RenderFolderDetails = (parameters: GridRenderCellParams<FolderContent>) => {
  if (parameters.row.contentType === 'asset') return;

  const { assetsCount, foldersCount, indexingCount = 0, id } = parameters.row;
  const text = [
    assetsCount > 0 && pluralize(assetsCount, 'Asset', `Assets`),
    foldersCount > 0 && pluralize(foldersCount, 'Folder', 'Folders'),
  ]
    .filter(Boolean)
    .join(', ');

  return indexingCount > 0 ? (
    <Box display='flex' flexDirection='row' alignItems='center' gap={'8px'}>
      <Tooltip title={`Indexing ${indexingCount}/${assetsCount} assets`}>
        <LinearProgress sx={{ width: '38px', flexShrink: 0 }} data-testid={`${id}-folder-indexing-progress`} />
      </Tooltip>
      <Ellipsis text={text} />
    </Box>
  ) : (
    <Ellipsis text={text} />
  );
};

export const RenderDetails = (parameters: GridRenderCellParams<FolderContent>) => {
  return parameters.row.contentType === 'asset' ? (
    <RenderAssetDetails {...parameters} />
  ) : (
    <RenderFolderDetails {...parameters} />
  );
};

export const RenderCreatedAt = (parameters: GridRenderCellParams<FolderContent>) => (
  <Ellipsis text={dateFormat(parameters.row.createdAt)} />
);

export const RenderCreator = (parameters: GridRenderCellParams<{ creator?: Creator }>) => {
  return <Ellipsis text={parameters.row.creator?.email ?? ''} />;
};

export const RenderFolder = (parameters: GridRenderCellParams<FolderContent>) => {
  const { getPath } = useTenants();
  const { permissions } = useGetCurrentUser();

  const asset = parameters.row as Asset;
  const folderPath = getAssetFolderDirectory(asset);
  if (permissions.canOnlySearch) {
    return <Ellipsis text={folderPath} />;
  }

  if (parameters.row.contentType === 'folder') {
    return;
  }

  return asset.folderId ? (
    <AssetLink to={getPath(`assets?folderId=${asset.folderId}`)} onClick={handleLinkClick}>
      <Ellipsis text={getAssetFolderDirectory(asset)} />
    </AssetLink>
  ) : (
    <AssetLink to={getPath(`assets`)} onClick={handleLinkClick}>
      <Ellipsis text='Home' />
    </AssetLink>
  );
};
